(() => {
  "use strict";

  const DESKTOP_BREAKPOINT = 1024;

  const SELECTORS = {
    section: ".article-recommended-section",
    embla: "[data-article-recommended]",
    prevButton: "[data-article-recommended-prev]",
    nextButton: "[data-article-recommended-next]",
  };

  /**
   * Initialize a single Embla carousel instance
   * @param {HTMLElement} emblaNode - The carousel container element
   * @returns {Object} Embla carousel instance
   */
  const initializeEmbla = (emblaNode) =>
    window.EmblaCarousel(emblaNode, {
      loop: false,
      align: "start",
      skipSnaps: false,
      dragFree: true,
      containScroll: "trimSnaps",
    });

  /**
   * Update navigation button states based on carousel position
   * @param {Object} embla - Embla carousel instance
   * @param {HTMLElement} prevButton - Previous button element
   * @param {HTMLElement} nextButton - Next button element
   */
  const updateButtonStates = (embla, prevButton, nextButton) => {
    if (!embla || !prevButton || !nextButton) return;

    prevButton.disabled = !embla.canScrollPrev();
    nextButton.disabled = !embla.canScrollNext();
  };

  /**
   * Initialize an article recommended section (carousel only on desktop)
   * @param {HTMLElement} section - The section container
   */
  const initializeSection = (section) => {
    const emblaNode = section.querySelector(SELECTORS.embla);
    const prevButton = section.querySelector(SELECTORS.prevButton);
    const nextButton = section.querySelector(SELECTORS.nextButton);

    if (!emblaNode || !window.EmblaCarousel) return;

    let embla = null;
    let resizeTimeout;

    const isDesktop = () =>
      window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`).matches;

    const setupCarousel = () => {
      if (!isDesktop()) return;

      if (embla) {
        embla.reInit();
      } else {
        embla = initializeEmbla(emblaNode);
        embla.on("select", () =>
          updateButtonStates(embla, prevButton, nextButton),
        );
        embla.on("reInit", () =>
          updateButtonStates(embla, prevButton, nextButton),
        );
        prevButton?.addEventListener("click", () => embla.scrollPrev());
        nextButton?.addEventListener("click", () => embla.scrollNext());
      }

      requestAnimationFrame(() =>
        updateButtonStates(embla, prevButton, nextButton),
      );
    };

    const teardownCarousel = () => {
      if (embla) {
        embla.destroy();
        embla = null;
      }
    };

    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (isDesktop()) {
          setupCarousel();
        } else {
          teardownCarousel();
        }
      }, 100);
    };

    window.addEventListener("resize", handleResize);

    if (isDesktop()) {
      setupCarousel();
    }
  };

  const init = () => {
    document.querySelectorAll(SELECTORS.section).forEach(initializeSection);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
